import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Header from "./Header";
import Figure from "./Figure";
import WrongLetters from "./WrongLetters";
import Word from "./Word";
import Popup from "./Popup";
import { showNotification as show, checkWin } from "./helpers";
import "./hangman.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { useAudio } from "../../../Providers/AudioProvider";
import Task from "../Task";
import { userGameRewards } from "../../../apis/user";
import { getHangmanQuestions } from "../../../apis/gameApi";
import Swal from "sweetalert2";
function Hangman() {
  const { userDetails, updateUserInfo, watchScreen, updatewatchScreenInfo } =
    useUserInfo();
  const playClick = useAudio();
  const task = "hangman";
  const [points, setPoints] = useState(0); // Keep for backward compatibility
  const [displayPoints, setDisplayPoints] = useState(0); // UI display points
  const [roundPoints, setRoundPoints] = useState(0); // Points for current round only
  const [totalPoints, setTotalPoints] = useState(0); // Total accumulated points for the day
  const [words, setWords] = useState([]);
  const [selectedWord, setSelectedWord] = useState("");
  const [selectedHint, setSelectedHint] = useState("");
  const [playable, setPlayable] = useState(true);
  const [correctLetters, setCorrectLetters] = useState([]);
  const [wrongLetters, setWrongLetters] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [usedWords, setUsedWords] = useState(new Set());
  const [gameOver, setGameOver] = useState(false);
  const hasScored = useRef(false);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const userId = userDetails?.userDetails?.telegramId;
  const apiCallInProgress = useRef(false); // Ref to track API call status
  const hasInteracted = useRef(false); // Track if user has made any guesses
  const apiCallMadeForCurrentRound = useRef(false); // Track if API call was made for current round
  const [showWinPopup, setShowWinPopup] = useState(false); // State for win popup
  const [showLosePopup, setShowLosePopup] = useState(false); // State for lose popup
  const [loadingNext, setLoadingNext] = useState(false); // State for next question loading
  const [questionSet, setQuestionSet] = useState(0); // Track which set of questions user is on
  const limit = 10; // Changed from 50 to 10 questions
  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;

  // Check daily play limit and question set on component mount
  useEffect(() => {
    // Initialize localStorage with count 0 if it doesn't exist
    const today = new Date().toDateString();
    const hangmanData = JSON.parse(
      localStorage.getItem(`hangman_plays_${userId}`)
    ) || { date: "", count: 0, set: 0 };

    if (hangmanData.date !== today) {
      // Reset count for new day
      localStorage.setItem(
        `hangman_plays_${userId}`,
        JSON.stringify({
          date: today,
          count: 0,
          set: questionSet, // Store current question set
        })
      );
    }

    checkDailyPlayLimit();
    initializeQuestionSet();
  }, []);

  // Function to initialize question set based on user's join date
  const initializeQuestionSet = () => {
    const today = new Date();
    const joinDate = new Date(userDetails?.userDetails?.joinDate || today);
    const daysSinceJoin = Math.floor(
      (today - joinDate) / (1000 * 60 * 60 * 24)
    );

    // Calculate which set of questions the user should be on
    // Each set has 10 questions, so divide days by 1 to get set number
    const calculatedSet = Math.floor(daysSinceJoin);

    // For new users (first day), start with set 0
    const userSet = daysSinceJoin === 0 ? 0 : calculatedSet;

    setQuestionSet(userSet);
  };

  // Function to check if the user has reached their daily play limit
  const checkDailyPlayLimit = () => {
    const today = new Date().toDateString();
    const hangmanData = JSON.parse(
      localStorage.getItem(`hangman_plays_${userId}`)
    ) || { date: "", count: 0, set: 0 };

    if (hangmanData.date !== today) {
      // Reset count for new day
      localStorage.setItem(
        `hangman_plays_${userId}`,
        JSON.stringify({
          date: today,
          count: 0,
          set: questionSet,
        })
      );
      setDailyLimitReached(false);
      setGameOver(false);
    } else if (hangmanData.count >= limit) {
      // User has reached the daily limit
      setDailyLimitReached(true);
      setPlayable(false);
      setGameOver(true); // Show game over popup immediately if limit is reached
    }
  };

  // Function removed - now integrated directly into handleApiResponse

  // Memoize the word list loading
  // useEffect(() => {
  //   fetch("/hangman_words_400.json")
  //     .then((res) => res.json())
  //     .then((data) => {
  //       // Remove duplicates by creating a Map with word as key
  //       const uniqueWords = Array.from(
  //         new Map(data.map((item) => [item.word, item])).values()
  //       );

  //       // Calculate start and end indices for current question set
  //       const wordsPerSet = limit; // Use the limit constant
  //       const startIndex = questionSet * wordsPerSet;
  //       const endIndex = startIndex + wordsPerSet;

  //       // Get the current set of words
  //       const currentSetWords = uniqueWords.slice(startIndex, endIndex);

  //       // Shuffle only the current set of words
  //       const shuffled = [...currentSetWords].sort(() => Math.random() - 0.5);

  //       setWords(shuffled);
  //       if (shuffled.length > 0) {
  //         setSelectedWord(shuffled[0].word.toLowerCase());
  //         setSelectedHint(shuffled[0].hint);
  //         setUsedWords(new Set([shuffled[0].word]));
  //       }
  //     })
  //     .catch((err) => console.error("Failed to load word list", err));
  // }, [questionSet]); 

  const loadHangmanQuestions = async () => {
    const today = new Date().toDateString();
    const cacheKey = `hangman_questions_${userId}`;
    const completionKey = `hangman_completed_${userId}`;
    
    try {
      // Check if we have cached questions for today
      const cachedData = localStorage.getItem(cacheKey);
      const cachedDate = localStorage.getItem(`${cacheKey}_date`);
      const isCompleted = localStorage.getItem(completionKey) === 'true';
      
      console.log("Cache check:", {
        hasCachedData: !!cachedData,
        cachedDate,
        today,
        isCompleted,
        isNewDay: cachedDate !== today
      });
      
      // If we have cached data for today and it's not completed, use it
      if (cachedData && cachedDate === today && !isCompleted) {
        console.log("Using cached questions for today");
        const data = JSON.parse(cachedData);
        setWords(data);
        if (data.length > 0) {
          setSelectedWord(data[0].word.toLowerCase());
          setSelectedHint(data[0].hint);
          setUsedWords(new Set([data[0].word]));
        }
        return;
      }
      
      // If it's a new day or no cached data, fetch from API
      if (cachedDate !== today || !cachedData) {
        console.log("Fetching new questions from API for new day");
        const response = await getHangmanQuestions();
        console.log("API Response:", response);
        
        // Extract questions array from the response
        const data = response?.questions || response;
        console.log("Extracted questions:", data);
        
        if (data && data.length > 0) {
          // Cache the new questions
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(`${cacheKey}_date`, today);
          localStorage.setItem(completionKey, 'false'); // Reset completion status
          
          setWords(data);
          if (data.length > 0) {
            setSelectedWord(data[0].word.toLowerCase());
            setSelectedHint(data[0].hint);
            setUsedWords(new Set([data[0].word]));
          }
        } else {
          console.error("No hangman questions received from API");
          setWords([]);
          setSelectedWord("");
          setSelectedHint("No questions available");
        }
      } else if (isCompleted) {
        console.log("All questions completed for today");
        setWords([]);
        setSelectedWord("");
        setSelectedHint("All questions completed for today! Come back tomorrow.");
      }
    } catch (err) {
      console.error("Failed to load hangman questions", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      // Fallback to empty state
      setWords([]);
      setSelectedWord("");
      setSelectedHint("Failed to load questions");
    }
  };
  useEffect(() => {
    loadHangmanQuestions();
  }, [userId]); // Load questions when userId changes

  const handleApiResponse = async (points) => {
    if (apiCallInProgress.current) {
      console.log("API call already in progress, skipping");
      return null;
    }

    // FIRST check if we've already reached the daily limit BEFORE making any API calls
    const currentData = JSON.parse(
      localStorage.getItem(`hangman_plays_${userId}`)
    ) || { date: new Date().toDateString(), count: 0 };
    
    if (currentData.count >= limit) {
      console.log("Daily limit already reached. Skipping API call.");
      setDailyLimitReached(true);
      setGameOver(true);
      setPlayable(false);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
      return null;
    }

    apiCallInProgress.current = true; // Set flag to indicate API call in progress
    
    // Disable gameplay immediately to prevent user interaction during API call
    setPlayable(false);

    // Set loading state first
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      // Create API data object exactly like in 2048 game
      const apiData = {
        telegramId: String(userDetails?.userDetails?.telegramId),
        gamePoints: points,
        gameType: "HangmanF",
        chancesToPlayed: 1, // Backend will add this up for each game
      };

      console.log("Sending API data:", apiData);
      console.log("Points being sent:", points);

      // Make the API call
      const res = await userGameRewards(apiData);

      // Handle response
      if (res && res.message === "Game rewards updated successfully") {
        console.log("API Response:", res);
        
        // Update loading state
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));

        // Update message from response
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));

        // Reset round points after API call (but don't reset totalPoints or displayPoints)
        setRoundPoints(0);

        // Update the play count in localStorage after successful API call
        const today = new Date().toDateString();
        const hangmanData = JSON.parse(
          localStorage.getItem(`hangman_plays_${userId}`)
        ) || { date: today, count: 0, set: questionSet };

        if (hangmanData.date === today) {
          // Increment count
          hangmanData.count += 1;
          localStorage.setItem(
            `hangman_plays_${userId}`,
            JSON.stringify(hangmanData)
          );
          
          // Check if this was the last allowed game (10th game)
          if (hangmanData.count >= limit) {
            setDailyLimitReached(true);
            setPlayable(false);
            setGameOver(true);
            console.log("All 10 questions completed. Game over.");
          }
        }
        
        // Reset API call flag after successful response
        apiCallInProgress.current = false;
        
        return res; // Return the response
      } else {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        
        // Re-enable gameplay if API fails (to allow retry)
        setPlayable(true);
        
        // Reset API call flag immediately on failure
        apiCallInProgress.current = false;
        
        return null;
      }
    } catch (error) {
      console.error("API call failed:", error);
      // Reset loading state on error
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
      
      // Re-enable gameplay if API fails (to allow retry)
      setPlayable(true);
      
      // Reset API call flag immediately on error
      apiCallInProgress.current = false;
      
      return null;
    }
  };

  // Memoize the win check effect with a more careful implementation
  useEffect(() => {
    // Don't proceed if no word selected or score already awarded
    if (!selectedWord || hasScored.current || !playable) return;

    // Check if we've already reached the daily limit BEFORE making any API calls
    const hangmanData = JSON.parse(
      localStorage.getItem(`hangman_plays_${userId}`)
    ) || { date: new Date().toDateString(), count: 0 };
    
    // IMPORTANT: If we've already reached 10 games, don't make any more API calls
    if (hangmanData.count >= limit) {
      setDailyLimitReached(true);
      setPlayable(false);
      setGameOver(true);
      return;
    }

    const result = checkWin(correctLetters, wrongLetters, selectedWord);

    if (result === "win") {
      // Only award points if we haven't scored for this round yet
      if (!hasScored.current) {
        // Mark that we've awarded points for this round FIRST
        // This prevents multiple points from being awarded during renders
        hasScored.current = true;
        
        // IMPORTANT: Disable gameplay immediately to prevent further interaction
        setPlayable(false);

        // Update points for win
        setRoundPoints(100);
        setTotalPoints((prevPoints) => prevPoints + 100);
        setDisplayPoints((prevPoints) => prevPoints + 100);

        // Show loading state for API call
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: true,
        }));

        // Double-check the limit again before making API call
        const currentData = JSON.parse(
          localStorage.getItem(`hangman_plays_${userId}`)
        ) || { date: new Date().toDateString(), count: 0 };
        
        // Check if this would exceed the limit (count is 0-based, so count 9 = 10th game)
        if (currentData.count >= limit) {
          // We've hit the limit, don't make API call
          console.log("Daily limit already reached. Skipping API call.");
          setDailyLimitReached(true);
          setGameOver(true);
          updateUserInfo((prev) => ({
            ...prev,
            isLoading: false,
          }));
          return;
        }

        // Make API call using async/await pattern
        if (!apiCallInProgress.current) {
          // Mark that API call is made for this round
          apiCallMadeForCurrentRound.current = true;
          
          // Create an async function and call it immediately
          (async () => {
            // Make API call and handle response with await
            const response = await handleApiResponse(100);
            
            if (response && response.message === "Game rewards updated successfully") {
              console.log("Win API call completed", response);
              
              // Show win popup after API call success
              setShowWinPopup(true);
              
              // User will need to click the Next Word button or X icon manually
              // No automatic transition to next question
            } else {
              // Re-enable gameplay if API fails
              setPlayable(true);
              hasScored.current = false;
            }
          })();
        }
      }
    } else if (result === "lose" && wrongLetters.length >= 6) {
      // Only award points if we haven't scored for this round yet
      if (!hasScored.current) {
        // Mark that this round is complete FIRST
        hasScored.current = true;
        
        // IMPORTANT: Disable gameplay immediately to prevent further interaction
        setPlayable(false);

        // Update points for lose
        setRoundPoints(10);
        setTotalPoints((prevPoints) => prevPoints + 10);
        setDisplayPoints((prevPoints) => prevPoints + 10);

        // Show loading state for API call
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: true,
        }));

        // Double-check the limit again before making API call
        const currentData = JSON.parse(
          localStorage.getItem(`hangman_plays_${userId}`)
        ) || { date: new Date().toDateString(), count: 0 };
        
        // Check if this would exceed the limit (count is 0-based, so count 9 = 10th game)
        if (currentData.count >= limit) {
          // We've hit the limit, don't make API call
          console.log("Daily limit already reached. Skipping API call.");
          setDailyLimitReached(true);
          setGameOver(true);
          updateUserInfo((prev) => ({
            ...prev,
            isLoading: false,
          }));
          return;
        }

        // Make API call using async/await pattern
        if (!apiCallInProgress.current) {
          // Mark that API call is made for this round
          apiCallMadeForCurrentRound.current = true;
          
          // Create an async function and call it immediately
          (async () => {
            // Make API call and handle response with await
            const response = await handleApiResponse(10);
            
            if (response && response.message === "Game rewards updated successfully") {
              console.log("Lose API call completed", response);
              
              // Show lose popup after API call success
              setShowLosePopup(true);
              
              // User will need to click the Next Word button or X icon manually
              // No automatic transition to next question
            } else {
              // Re-enable gameplay if API fails
              setPlayable(true);
              hasScored.current = false;
            }
          })();
        }
      }
    }
  }, [correctLetters, wrongLetters, selectedWord, points, playable, userId, limit]);

  // Memoize the handleGuess function with better check for existing letters
  const handleGuess = useCallback(
    (letter) => {
      // Check if daily limit reached first
      const hangmanData = JSON.parse(
        localStorage.getItem(`hangman_plays_${userId}`)
      ) || { date: new Date().toDateString(), count: 0 };
      if (hangmanData.count >= limit) {
        setDailyLimitReached(true);
        setPlayable(false);
        setGameOver(true);
        return;
      }

      // IMPORTANT: Don't allow guesses during API calls or when game is not playable
      if (!playable || dailyLimitReached || hasScored.current || apiCallInProgress.current) return;

      // Mark that user has interacted with the game
      hasInteracted.current = true;

      // Check if letter is already guessed to prevent state updates
      const isAlreadyCorrect = correctLetters.includes(letter);
      const isAlreadyWrong = wrongLetters.includes(letter);

      if (isAlreadyCorrect || isAlreadyWrong) {
        show(setShowNotification);
        return; // Don't update state if already guessed
      }

      if (selectedWord.includes(letter)) {
        setCorrectLetters((current) => [...current, letter]);
      } else {
        setWrongLetters((current) => [...current, letter]);
      }
    },
    [
      playable,
      selectedWord,
      correctLetters,
      wrongLetters,
      dailyLimitReached,
      userId,
    ]
  );

  // Memoize the playAgain function
  const playAgain = useCallback(() => {
    // Add debug logging to track when playAgain is called
    console.log("playAgain called - Manual navigation to next question");
    console.trace("playAgain call stack trace");
    
    // Check if API call is in progress
    if (apiCallInProgress.current) {
      console.log("API call in progress, cannot start new game yet");
      return;
    }

    // Show loading state for next question
    setLoadingNext(true);
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));

    // Reset the scoring flag
    hasScored.current = false;
    
    // Reset API call flag for new round
    apiCallMadeForCurrentRound.current = false;

    // Reset popups
    setShowWinPopup(false);
    setShowLosePopup(false);

    // Check daily limit before allowing new game
    const hangmanData = JSON.parse(
      localStorage.getItem(`hangman_plays_${userId}`)
    ) || { date: new Date().toDateString(), count: 0 };

    if (hangmanData.count >= limit) {
      setDailyLimitReached(true);
      setPlayable(false);
      setLoadingNext(false);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
      return;
    }

    // Reset for new game
    setPlayable(true);
    setCorrectLetters([]);
    setWrongLetters([]);
    setRoundPoints(0); // Reset only round points, keep totalPoints

    if (words.length > 1) {
      // Find the next unused word
      const nextWord = words.find((word) => !usedWords.has(word.word));

      if (nextWord) {
        setSelectedWord(nextWord.word.toLowerCase());
        setSelectedHint(nextWord.hint);
        setUsedWords((prev) => new Set([...prev, nextWord.word]));
      } else {
        // If all words are used, mark as completed and show completion message
        const completionKey = `hangman_completed_${userId}`;
        localStorage.setItem(completionKey, 'true');
        console.log("All 10 questions completed for today!");
        
        setSelectedWord("");
        setSelectedHint("Congratulations! You've completed all words! 🎉");
      }
    } else {
      setSelectedWord("");
      setSelectedHint("No more words available. Refresh to restart!");
    }

    // Hide loader after a short delay to ensure smooth transition
    setTimeout(() => {
      setLoadingNext(false);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }, 300);
  }, [words, usedWords, points, userId, limit]);

  // Memoize the keyboard event handler
  useEffect(() => {
    const handleKeydown = (event) => {
      const { key, keyCode } = event;
      if (playable && !dailyLimitReached && keyCode >= 65 && keyCode <= 90) {
        handleGuess(key.toLowerCase());
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [handleGuess, playable, dailyLimitReached]);

  // Memoize the keyboard buttons
  const keyboardButtons = useMemo(() => {
    return "abcdefghijklmnopqrstuvwxyz".split("").map((letter) => (
      <button
        key={letter}
        onClick={() => handleGuess(letter)}
        disabled={
          correctLetters.includes(letter) ||
          wrongLetters.includes(letter) ||
          !playable ||
          dailyLimitReached
        }
        className="hangman-key-button"
        style={{ textTransform: "uppercase" }}
      >
        {letter.toUpperCase()}
      </button>
    ));
  }, [handleGuess, correctLetters, wrongLetters, playable, dailyLimitReached]);

  const handleBack = useCallback(() => {
    // Ask for confirmation if points have been earned
    if (totalPoints > 0) {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you want to exit the game?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Exit",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          // User chose to continue - do nothing
          console.log("User chose to continue");
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          // User chose to exit - submit current score
          console.log("User chose to exit, submitting score:", totalPoints);

          // Submit the current total points value
          if (!apiCallInProgress.current) {
            handleApiResponse(totalPoints);
          }

          // Navigate back after a short delay to ensure API call is made
          setTimeout(() => {
            toogleMenu(Task, "task");
          }, 500);
        }
      });
    } else {
      // No points, just go back
      toogleMenu(Task, "task");
    }
  }, [totalPoints]);

  const toogleMenu = (task, currentComponentText) => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: Task,
      currentComponentText: "task",
      lastComponent: userDetails?.currentComponent,
      lastComponentText: userDetails?.currentComponentText,
      isMenu: true,
      menuCount: (userDetails?.menuCount || 0) + 1,
    }));
  };

  return (
    <div
      className="hangman-game-container"
      style={{ height: "100vh", pointerEvents: "auto" }}
    >
      <div style={{ position: "relative" }}>
        <img
          onClick={handleBack}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          className="cancel-full "
          style={{ cursor: "pointer" }}
        />
      </div>
      <Header />
      <div className="hangman-header">
        <h1 className="welcometextmeme" style={{ fontSize: "18px" }}>
          HANGMAN
        </h1>
        <p style={{ color: "#ffffff" }}>FIND THE HIDDEN WORD - SAVE THE MAN</p>
      </div>
      {/* <div className="hangman-points-display" style={{ marginTop: "0px" }}>
        <h2>
          {IsPointsReached ? "SCORE" : "TOKENS"}: {displayPoints}
        </h2>
        {!dailyLimitReached ? (
          <h3 style={{ fontSize: "14px", color: "#aaa" }}>
            WORDS:{" "}
            {Math.min(
              JSON.parse(localStorage.getItem(`hangman_plays_${userId}`))
                ?.count || 0,
              limit
            )}
            /10
          </h3>
        ) : (
          <h3
            style={{
              fontSize: "14px",
              color: "#ff6666",
              textTransform: "uppercase",
            }}
          >
            Daily limit reached! Come back tomorrow.
          </h3>
        )}
      </div> */}
      <WrongLetters wrongLetters={wrongLetters} />
      <h2 style={{ 
        fontSize: window.innerWidth > 720 ? "21px" : "18px", 
        color: "green"
      }}>
        {IsPointsReached ? "SCORE" : "TOKENS"}: {displayPoints}
      </h2>
      <Figure wrongLetters={wrongLetters} />
      <h3 style={{ 
        fontSize: window.innerWidth > 720 ? "21px" : "18px", 
        color: "green"
      }}>
        WORDS:{" "}
        {Math.min(
          JSON.parse(localStorage.getItem(`hangman_plays_${userId}`))?.count ||
            0,
          limit
        )}
        /10
      </h3>
      {selectedWord ? (
        <>
          <Word selectedWord={selectedWord} correctLetters={correctLetters} />
          <p className="hangman-hint">Hint: {selectedHint}</p>
          <div className="hangman-keyboard">{keyboardButtons}</div>
        </>
      ) : (
        <h3 style={{ textAlign: "center" }}>No more words left. 🎉</h3>
      )}

      {/* Win popup */}
      {hasInteracted.current &&
        !gameOver &&
        !dailyLimitReached &&
        showWinPopup && (
          <div className="overlay">
            <div className="full-popup">
              <div className="popup-content">
                <h2 className="epic">CORRECT!</h2>
                {/* X icon - disabled during API calls */}
                <img
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                  className="cancel-img"
                  onClick={() => !apiCallInProgress.current && playAgain()}
                  style={{ 
                    cursor: apiCallInProgress.current ? 'not-allowed' : 'pointer',
                    opacity: apiCallInProgress.current ? 0.5 : 1
                  }}
                />
                <div className="row text-center">
                  <div className="col-12">
                    <div className="epic-div" style={{ padding: "30px 40px" }}>
                      <p className="pointsLabel">TOKENS EARNED</p>
                      <div className="pointsValue1">
                        <img
                          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                          alt="coin"
                        />
                        <p
                          className="pointsNumber"
                          style={{ marginBottom: "0px" }}
                        >
                          100
                        </p>
                      </div>
                      {/* Next Word button - disabled during API calls */}
                      <button
                        onClick={() => !apiCallInProgress.current && playAgain()}
                        disabled={apiCallInProgress.current}
                        style={{
                          padding: "10px 20px",
                          backgroundColor: "#00ff66",
                          color: "#000",
                          border: "none",
                          borderRadius: "5px",
                          fontWeight: "bold",
                          cursor: apiCallInProgress.current ? 'not-allowed' : 'pointer',
                          margin: "10px 0",
                          opacity: apiCallInProgress.current ? 0.5 : 1
                        }}
                      >
                        {apiCallInProgress.current ? "PROCESSING..." : "NEXT WORD"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Lose popup */}
      {hasInteracted.current &&
        !gameOver &&
        !dailyLimitReached &&
        showLosePopup && (
          <div className="overlay">
            <div className="full-popup">
              <div className="popup-content">
                <h2 className="epic">WRONG GUESS!</h2>
                {/* X icon - disabled during API calls */}
                <img
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                  className="cancel-img"
                  onClick={() => !apiCallInProgress.current && playAgain()}
                  style={{ 
                    cursor: apiCallInProgress.current ? 'not-allowed' : 'pointer',
                    opacity: apiCallInProgress.current ? 0.5 : 1
                  }}
                />
                <div className="row text-center">
                  <div className="col-12">
                    <div className="epic-div" style={{ padding: "30px 40px" }}>
                      <p
                        className="pointsLabel"
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          marginBottom: "10px",
                        }}
                      >
                        THE WORD WAS:
                      </p>
                      <p
                        style={{
                          fontSize: "24px",
                          color: "#00ff66",
                          fontWeight: "bold",
                          margin: "10px 0 20px 0",
                        }}
                      >
                        {selectedWord.toUpperCase()}
                      </p>
                      <p className="pointsLabel">TOKENS EARNED</p>
                      <div className="pointsValue1">
                        <img
                          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                          alt="coin"
                        />
                        <p
                          className="pointsNumber"
                          style={{ marginBottom: "0px" }}
                        >
                          10
                        </p>
                      </div>
                      {/* Add Next Word button to lose popup too */}
                      <button
                        onClick={() => !apiCallInProgress.current && playAgain()}
                        disabled={apiCallInProgress.current}
                        style={{
                          padding: "10px 20px",
                          backgroundColor: "#00ff66",
                          color: "#000",
                          border: "none",
                          borderRadius: "5px",
                          fontWeight: "bold",
                          cursor: apiCallInProgress.current ? 'not-allowed' : 'pointer',
                          margin: "10px 0",
                          opacity: apiCallInProgress.current ? 0.5 : 1
                        }}
                      >
                        {apiCallInProgress.current ? "PROCESSING..." : "NEXT WORD"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {gameOver && !IsPointsReached && !dailyLimitReached && (
        <div className="overlay">
          <div className="full-popup">
            <div className="popup-content">
              <h2 className="epic">Game Over!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="cancel-img"
                onClick={() => setGameOver(false)}
              />
              <div className="row text-center">
                <div className="col-12">
                  <div className="epic-div" style={{ padding: "30px 40px" }}>
                    <p className="pointsLabel">TOKENS EARNED</p>
                    <div className="pointsValue1">
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                        alt="coin"
                      />
                      <p className="pointsNumber">{displayPoints}</p>
                    </div>
                  </div>
                  <p className="bottom-text">
                    Play daily to boost your score and rack up more points!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {gameOver && IsPointsReached && !dailyLimitReached && (
        <div className="overlay">
          <div className="full-popup">
            <div className="popup-content">
              <h2 className="epic">Game Over!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="cancel-img"
                onClick={() => setGameOver(false)}
              />
              <div className="row text-center">
                <div className="col-12">
                  <div className="epic-div">
                    <p
                      className="pointsLabel"
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        margin: "20px 0",
                        textAlign: "center",
                      }}
                    >
                      POINTS EARNED: {displayPoints} <br />
                      TOKENS EARNED: NIL
                    </p>
                  </div>
                  <p className="bottom-text">
                    KEEP GUESSING! 🎮 SOLVE MORE WORDS AND CHASE THE HIGH SCORE!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {dailyLimitReached && (
        <div className="overlay">
          <div className="full-popup">
            <div className="popup-content">
              <h2 className="epic">Daily Limit Reached</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="cancel-img"
                onClick={handleBack}
                style={{ top: "5px" }}
              />
              <div className="row text-center">
                <div className="col-12">
                  <div className="epic-div">
                    <p
                      className="pointsLabel"
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        margin: "20px 0",
                        textAlign: "center",
                      }}
                    >
                      YOU'VE PLAYED 10 GAMES TODAY!
                      <br />
                      COME BACK TOMORROW FOR MORE.
                    </p>
                  </div>
                  <p className="bottom-text">
                    RETURN TOMORROW TO TEST YOUR WORD-GUESSING SKILLS AGAIN! 🎮
                  </p>
                  <button
                    onClick={handleBack}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#00ff66",
                      color: "#000",
                      border: "none",
                      borderRadius: "5px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      margin: "20px 0 10px 0",
                      width: "80%",
                      maxWidth: "200px",
                    }}
                  >
                    EXIT TO GAMES
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add keyframe animation for loader */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

export default React.memo(Hangman);
// ------END OF HANGMAN GAME------
